import {Draft} from 'immer';
import React from 'react';
import {NodeAddChange, NodeRemoveChange} from 'reactflow';

import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getIncrementedNodeName} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/nodeUtils';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {FlowchartChanges} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/FlowchartStateTypes';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {FlowchartStaticEdgeData, ProcessFlowchartEdgeType} from '../processFlowchartConfigEditorConstants';
import {
  isStaticEndNodeData,
  isStaticRouterNodeData,
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
} from '../processFlowchartConfigEditorNodeTypes';
import {
  DeletedNodeContext,
  EdgeRemoveChangeWithDeletedNodeContext,
  FlowchartNodeChange,
  FlowchartNodeChangeType,
} from '../processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {resetCustomMappingsForChangedFields} from '../types/transition/transitionLifecycleUtils';
import {extractStaticData} from './extractStaticData';
import {getChangedFieldTypeClientIds} from './fieldDefinitionUtils';
import {getEmptyFlowchartChanges} from './flowchartChangesUtils';

/**
 * Builds edge removal side effects for all edges connected to a deleted node.
 * When the deleted node is a router, resolves the upstream task's PTDV so that
 * outgoing edges (Router → TaskX) can identify the effective PTDV pair for
 * transition orphan detection.
 */
function buildEdgeRemovalsForDeletedNode(
  deletedNodeId: string,
  orphanedEdges: Array<FlowchartStaticEdgeData>,
  nodes: Array<StaticFlowchartNodeData>,
  removedPTDVId: string | null,
  removedNodeWasRouter: boolean
): Array<EdgeRemoveChangeWithDeletedNodeContext> {
  let routerUpstreamPTDVId: string | undefined;
  if (removedNodeWasRouter) {
    const incomingCFEdge = orphanedEdges.find(
      (e) => e.target === deletedNodeId && e.type === ProcessFlowchartEdgeType.CONTROL_FLOW
    );
    if (incomingCFEdge != null) {
      const upstreamNode = nodes.find((n) => n.id === incomingCFEdge.source);
      if (upstreamNode != null && isStaticTaskSchemaNodeData(upstreamNode)) {
        routerUpstreamPTDVId = upstreamNode.taskDefinitionVersionSpec.clientId;
      }
    }
  }

  return orphanedEdges.map((edge) => {
    const isSourceDeleted = edge.source === deletedNodeId;
    const isTargetDeleted = edge.target === deletedNodeId;
    const deletedNodeContext: DeletedNodeContext = {
      deletedSourceNodePTDVId: isSourceDeleted
        ? removedNodeWasRouter
          ? routerUpstreamPTDVId
          : (removedPTDVId ?? undefined)
        : undefined,
      deletedTargetNodePTDVId: isTargetDeleted ? (removedPTDVId ?? undefined) : undefined,
      deletedNodeWasRouter: removedNodeWasRouter || undefined,
    };
    return {type: 'remove' as const, id: edge.id, deletedNodeContext};
  });
}

function applyAddNodeChange(
  change: NodeAddChange,
  draft: Draft<FlowchartConfigWithMetadata>,
  setNodePositions: React.Dispatch<React.SetStateAction<Record<string, {x: number; y: number}>>>,
  shouldIncrementNodeNames: boolean
): void {
  if (change.item == null) {
    return;
  }
  let newNodeData: StaticFlowchartNodeData = extractStaticData(change.item.data);
  const existingNodeNames = shouldIncrementNodeNames
    ? draft.staticFlowchart.nodesStaticData.map((node) => node.nodeName)
    : undefined;
  if (isStaticRouterNodeData(newNodeData) || isStaticEndNodeData(newNodeData)) {
    const incrementedName = getIncrementedNodeName(newNodeData.nodeName, existingNodeNames);
    newNodeData = {...newNodeData, nodeName: incrementedName};
  } else if (isStaticTaskSchemaNodeData(newNodeData)) {
    const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(
      newNodeData.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
    );
    const nodeName = adapter.getNodeName(
      newNodeData.taskDefinitionVersionSpec,
      newNodeData.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
      existingNodeNames
    );
    newNodeData = {...newNodeData, nodeName};
  }
  draft.staticFlowchart.nodesStaticData.push(newNodeData);
  setNodePositions((prevPositions) => ({
    ...prevPositions,
    [change.item.id]: change.item.position || {x: 0, y: 0},
  }));
}

function applyRemoveNodeChange(
  change: NodeRemoveChange,
  draft: Draft<FlowchartConfigWithMetadata>,
  setNodePositions: React.Dispatch<React.SetStateAction<Record<string, {x: number; y: number}>>>,
  sideEffects: FlowchartChanges
): void {
  const removedNode = draft.staticFlowchart.nodesStaticData.find((node) => node.id === change.id);
  assert(removedNode != null, 'Node does not exist in static data');

  const removedPTDVId = isStaticTaskSchemaNodeData(removedNode)
    ? removedNode.taskDefinitionVersionSpec.clientId
    : null;
  const removedNodeWasRouter = isStaticRouterNodeData(removedNode);

  draft.staticFlowchart.nodesStaticData = draft.staticFlowchart.nodesStaticData.filter(
    (node) => node.id !== change.id
  );
  draft.metadata.interactionState.selectedNodeIds.delete(change.id);
  setNodePositions((prevPositions) => {
    const newPositions = {...prevPositions};
    delete newPositions[change.id];
    return newPositions;
  });

  const orphanedEdges = draft.staticFlowchart.edgesStaticData.filter(
    (edge) => edge.source === change.id || edge.target === change.id
  );
  sideEffects.edgeChanges.push(
    ...buildEdgeRemovalsForDeletedNode(
      change.id,
      orphanedEdges,
      draft.staticFlowchart.nodesStaticData,
      removedPTDVId,
      removedNodeWasRouter
    )
  );
}

function applyReplaceNodeChange(
  change: Extract<FlowchartNodeChange, {type: FlowchartNodeChangeType.REPLACE}>,
  draft: Draft<FlowchartConfigWithMetadata>
): void {
  const nodeToReplace = draft.staticFlowchart.nodesStaticData.find((node) => node.id === change.id);
  if (nodeToReplace == null) {
    return;
  }
  let newNodeData: StaticFlowchartNodeData = extractStaticData(change.data);
  if (isStaticTaskSchemaNodeData(nodeToReplace) && newNodeData.nodeName === '') {
    const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(
      nodeToReplace.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
    );
    const defaultName = adapter.getNodeName(
      nodeToReplace.taskDefinitionVersionSpec,
      nodeToReplace.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
      draft.staticFlowchart.nodesStaticData.map((node) => node.nodeName)
    );
    newNodeData = {...newNodeData, nodeName: defaultName};
  }
  if (isStaticTaskSchemaNodeData(nodeToReplace) && isStaticTaskSchemaNodeData(newNodeData)) {
    const changedFieldClientIds = getChangedFieldTypeClientIds(
      nodeToReplace.taskDefinitionVersionSpec.inputFieldDefinitions,
      newNodeData.taskDefinitionVersionSpec.inputFieldDefinitions,
      nodeToReplace.taskDefinitionVersionSpec.outputFieldDefinitions,
      newNodeData.taskDefinitionVersionSpec.outputFieldDefinitions
    );
    if (changedFieldClientIds.size > 0) {
      resetCustomMappingsForChangedFields(
        draft.metadata.transitions,
        nodeToReplace.taskDefinitionVersionSpec.clientId,
        changedFieldClientIds
      );
    }
  }
  Object.assign(nodeToReplace, newNodeData);

  // Keep metadata.rootTaskSchema in sync when the root node's fields change.
  // The node's taskSchema is typed as TaskSchemaSpec but at runtime it's the
  // full ProcessTaskSchemaGraphQL (set by cacheUpdatedTaskSchemaAndUpdateNode).
  if (
    isStaticTaskSchemaNodeData(nodeToReplace) &&
    nodeToReplace.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS &&
    draft.metadata.rootTaskSchema != null
  ) {
    const updatedTaskSchema =
      nodeToReplace.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema;
    // Only sync when we have the full GQL schema (indicated by __typename).
    // In the transition modal, nodes are built from bare TaskSchemaSpec without
    // __typename, so we skip the sync.
    if ((updatedTaskSchema as {__typename?: string}).__typename === 'ProcessTaskSchemaNode') {
      draft.metadata.rootTaskSchema = updatedTaskSchema as ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
    }
  }
}

export function applyFlowchartNodeChanges(
  nodeChanges: Array<FlowchartNodeChange>,
  draft: Draft<FlowchartConfigWithMetadata>,
  setNodePositions: React.Dispatch<React.SetStateAction<Record<string, {x: number; y: number}>>>,
  shouldIncrementNodeNames: boolean = true
): FlowchartChanges {
  const sideEffects = getEmptyFlowchartChanges();
  nodeChanges.forEach((change) => {
    switch (change.type) {
      case FlowchartNodeChangeType.ADD:
        applyAddNodeChange(change, draft, setNodePositions, shouldIncrementNodeNames);
        break;
      case FlowchartNodeChangeType.POSITION:
        if (change.position) {
          setNodePositions((prevPositions) => ({
            ...prevPositions,
            [change.id]: change.position as {x: number; y: number},
          }));
        }
        break;
      case FlowchartNodeChangeType.SELECT: {
        const isCurrentlySelected = draft.metadata.interactionState.selectedNodeIds.has(change.id);
        if (change.selected && !isCurrentlySelected) {
          draft.metadata.interactionState.selectedNodeIds.add(change.id);
        } else if (!change.selected && isCurrentlySelected) {
          draft.metadata.interactionState.selectedNodeIds.delete(change.id);
        }
        break;
      }
      case FlowchartNodeChangeType.SELECT_ROW: {
        const isCurrentlySelected = draft.metadata.interactionState.selectedRowIds.has(change.rowId);
        if (change.selected && !isCurrentlySelected) {
          draft.metadata.interactionState.selectedRowIds.add(change.rowId);
        } else if (!change.selected && isCurrentlySelected) {
          draft.metadata.interactionState.selectedRowIds.delete(change.rowId);
        }
        break;
      }
      case FlowchartNodeChangeType.REMOVE:
        applyRemoveNodeChange(change, draft, setNodePositions, sideEffects);
        break;
      case FlowchartNodeChangeType.REPLACE:
        applyReplaceNodeChange(change, draft);
        break;
      case FlowchartNodeChangeType.RESET:
        // TODO: Explore if reset changes need to be handled?
        break;
      default:
        assert(false, 'Unexpected node change type', {change});
    }
  });

  return sideEffects;
}
